/**
 * D18: before a pasted client email or WhatsApp message goes to Ask, code
 * strips what isn't the request itself — earlier quoted replies, forwarded
 * headers and the signature — so the AI sees only the logistics. Phones and
 * emails are masked again on the server; nothing here is stored.
 */

const QUOTE_START = [
    /^>/,                                                   // "> quoted line"
    /^on .{3,120} wrote:\s*$/i,                             // "On Mon, 3 Nov, Sara wrote:"
    /^-{2,}\s*(original|forwarded) message\s*-{2,}/i,      // "----- Original Message -----"
    /^(from|sent|to|cc|subject|date)\s*:\s/i,               // forwarded / reply header block
    /^_{5,}\s*$/,                                           // Outlook divider
    /^\[\d{1,2}[/.]\d{1,2}[/.]\d{2,4},?\s+\d{1,2}:\d{2}/,  // WhatsApp export line "[03/11/2026, 10:15]" of an older message
];

const SIGNATURE_START = [
    /^--\s*$/,
    /^(best|kind|warm)?\s*regards,?\s*$/i,
    /^(thanks|thank you|many thanks|cheers|sincerely|br),?\s*$/i,
    /^sent from my (iphone|android|samsung|mobile|phone)/i,
    /^مع (أطيب|خالص) التحيات/,
    /^(شكرا|شكراً)[،,]?\s*$/,
    // "شكراً — ليلى", "Thanks - Sara": the sign-off with the name on one line
    /^(?:شكرا|شكراً|thanks|thank you|regards|best|cheers)\s*[،,]?\s*[—–-]\s*[\p{L} .'-]{1,40}$/iu,
];

/** The pasted email's own header lines (before the body): From, To, Cc, Date, Sent, Subject. */
const OWN_HEADER = /^(from|sent|to|cc|bcc|date|subject)\s*:\s/i;
/** A forwarded email pasted from its top ("---------- Forwarded message ---------"): the original's header follows. */
const FORWARD_MARK = /^(?:-{2,}\s*forwarded message\s*-{2,}|begin forwarded message:?)\s*$/i;
// Western or Arabic-Indic digits, as phones export them
const DIGIT = "[0-9\\u0660-\\u0669\\u06F0-\\u06F9]";
const DATE = `${DIGIT}{1,2}[/.]${DIGIT}{1,2}[/.]${DIGIT}{2,4}`;
const TIME = `${DIGIT}{1,2}:${DIGIT}{2}(?::${DIGIT}{2})?\\s*(?:[ap]\\.?m\\.?|ص|م)?`;
/** A WhatsApp export line: "[13/10/2026, 09:14] Layla:" (iPhone) or "13/10/2026, 09:14 - Layla:" (Android); Arabic comma too. */
const WHATSAPP_LINE = new RegExp(`^\\u200e?(?:\\[${DATE}[,،]?\\s+${TIME}\\]|${DATE}[,،]?\\s+${TIME}\\s+[-–])\\s*`, "i");
const SENDER = /^([^:\n]{1,40}):\s/;

const exportLines = (text: string) => String(text ?? "").split(/\r?\n/).map((raw) => raw.replace(/[‎‏]/g, "").trim());

/** Who wrote in a pasted WhatsApp export, in the order they first wrote (a name, or the number shown for unsaved contacts). */
export const chatSenders = (text: string): string[] => {
    const senders: string[] = [];
    for (const line of exportLines(text)) {
        const stamp = WHATSAPP_LINE.exec(line);
        const name = stamp ? SENDER.exec(line.slice(stamp[0].length))?.[1]?.trim() : null;
        if (name && !senders.includes(name)) senders.push(name);
    }
    return senders;
};

/** A pasted WhatsApp export with the time stamps and sender names taken off each message (other text as it was). */
export const withoutChatStamps = (text: string): string => exportLines(text)
    .map((line) => {
        const stamp = WHATSAPP_LINE.exec(line);
        return stamp ? line.slice(stamp[0].length).replace(SENDER, "") : line;
    })
    .join("\n");

/** The pasted email's own header (the lines above the body). */
const topHeader = (text: string): string[] => {
    const header: string[] = [];
    for (const line of exportLines(text)) {
        if (!line && !header.length) continue;
        if (!header.length && FORWARD_MARK.test(line)) continue;
        if (!OWN_HEADER.test(line)) break;
        header.push(line);
    }
    return header;
};

/** The name on a pasted email's From line ("From: Layla Haddad <l.haddad@…>"). */
export const emailSender = (text: string): string | null => {
    for (const line of topHeader(text)) {
        const from = /^from\s*:\s*"?([^"<\n@]{2,60}?)"?\s*<[^>\n]*@[^>\n]*>/i.exec(line);
        if (from) return from[1].trim();
    }
    return null;
};

/** The pasted email's subject, if its header was pasted too. */
export const emailSubject = (text: string): string | null => {
    for (const line of topHeader(text)) {
        const subject = /^subject\s*:\s*(.+)$/i.exec(line);
        if (subject) return subject[1].trim().slice(0, 200);
    }
    return null;
};

/** Whether the text starts with an email header (From, To, Subject…). */
export const hasEmailHeader = (text: string): boolean => topHeader(text).length > 0;

/** The senders of a pasted chat, else the name on a pasted email's From line. */
export const messageSenders = (text: string): string[] => {
    const chat = chatSenders(text);
    if (chat.length) return chat;
    const from = emailSender(text);
    return from ? [from] : [];
};

/**
 * The message without quoted replies, forwarded headers or the signature.
 * The pasted email's own header (From, To, Date…) is dropped and its
 * Subject kept; a WhatsApp export keeps every message, without the time
 * stamps and sender names.
 * @param text - What was pasted.
 * @param max - Longest result kept (the question limit leaves room for the instruction).
 */
export const stripMessage = (text: string, max = 1200): string => {
    const kept: string[] = [];
    let started = false;
    let isWhatsApp = false;
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (!started && !line) continue;
        if (!started && !kept.length && FORWARD_MARK.test(line)) continue;
        if (!started && OWN_HEADER.test(line)) {
            if (/^subject\s*:/i.test(line)) kept.push(line);
            continue;
        }
        if (!started && WHATSAPP_LINE.test(line)) isWhatsApp = true;
        if (isWhatsApp && WHATSAPP_LINE.test(line)) {
            started = true;
            kept.push(line.replace(WHATSAPP_LINE, "").replace(/^[^:\n]{1,40}:\s*/, ""));
            continue;
        }
        if (QUOTE_START.some((pattern) => pattern.test(line))) break;
        if (SIGNATURE_START.some((pattern) => pattern.test(line))) break;
        if (line) started = true;
        kept.push(raw.trimEnd());
    }
    return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim().slice(0, max);
};
